import type { ReleaseListGroup } from '../../components/ReleaseList'
import { haptic } from '../../utils/haptics'
import { getCollectionSideIndexLabel } from './collectionGrouping'

type IndexSlot = { from: number; to: number }

const MAX_INDEX_SLOTS = 17
const NAVIGATION_CLEARANCE = 8

export class CollectionSideIndex {
	private readonly listeners = new AbortController()
	private pointerId: number | null = null
	private selectedButton: HTMLButtonElement | null = null
	private pendingGroups: ReleaseListGroup[] | null = null
	private groups: ReleaseListGroup[] = []
	private selectedGroupIndex: number | null = null
	private offsetFrame = 0

	constructor(
		private readonly rootEl: HTMLElement,
		private readonly listEl: HTMLElement | null,
	) {
		const options = { signal: this.listeners.signal }
		rootEl.addEventListener('pointerdown', this.handlePointerDown, options)
		rootEl.addEventListener('pointermove', this.handlePointerMove, options)
		rootEl.addEventListener('pointerup', this.handlePointerEnd, options)
		rootEl.addEventListener('pointercancel', this.handlePointerEnd, options)
		rootEl.addEventListener(
			'lostpointercapture',
			this.handlePointerEnd,
			options,
		)
		rootEl.addEventListener('click', this.handleClick, options)
		rootEl.addEventListener('keydown', this.handleKeydown, options)
		window.addEventListener('resize', this.handleResize, options)
		window.addEventListener('scroll', this.scheduleOffsetUpdate, {
			...options,
			passive: true,
		})
	}

	render(groups: ReleaseListGroup[]): void {
		// Pagination must not move the letters beneath a held finger.
		if (this.pointerId !== null) {
			this.pendingGroups = groups
			return
		}
		this.groups = groups
		const slots = buildIndexSlots(groups.length, this.maxSlots())
		const existing = new Map(
			Array.from(
				this.rootEl.querySelectorAll<HTMLButtonElement>('button'),
			).map((button) => [button.dataset.collectionSideIndexKey, button]),
		)
		slots.forEach(({ from, to }, index) => {
			const group = groups[from]
			const lastGroup = groups[to]
			const key = `${group.id}:${lastGroup.id}`
			const button = existing.get(key) ?? document.createElement('button')
			button.type = 'button'
			button.className = 'collection-side-index-item'
			const skipped = from !== to
			const label = skipped
				? `${group.label} – ${lastGroup.label}`
				: group.label
			button.textContent = skipped
				? '•'
				: (group.sideIndexLabel ??
					getCollectionSideIndexLabel(group.label))
			button.setAttribute('aria-label', label)
			button.title = label
			button.dataset.collectionSideIndexTarget = group.id
			button.dataset.collectionSideIndexKey = key
			button.dataset.indexFrom = String(from)
			button.dataset.indexTo = String(to)
			if (this.rootEl.children[index] !== button) {
				this.rootEl.insertBefore(
					button,
					this.rootEl.children[index] ?? null,
				)
			}
		})
		while (this.rootEl.children.length > slots.length)
			this.rootEl.lastElementChild?.remove()
		this.rootEl.hidden = groups.length === 0
		this.scheduleOffsetUpdate()
	}

	cancelDrag(): void {
		const pointerId = this.pointerId
		this.pointerId = null
		if (pointerId !== null && this.rootEl.hasPointerCapture(pointerId)) {
			this.rootEl.releasePointerCapture(pointerId)
		}
		this.selectedButton?.classList.remove('is-active')
		this.selectedButton = null
		this.selectedGroupIndex = null
		if (this.pendingGroups) {
			const groups = this.pendingGroups
			this.pendingGroups = null
			this.render(groups)
		}
	}

	dispose(): void {
		this.listeners.abort()
		cancelAnimationFrame(this.offsetFrame)
		this.pendingGroups = null
		this.cancelDrag()
	}

	private handlePointerDown = (event: PointerEvent): void => {
		if (!event.isPrimary || event.button !== 0 || this.pointerId !== null)
			return
		event.preventDefault()
		event.stopPropagation()
		this.pointerId = event.pointerId
		// Capture on the persistent index, not a letter that pagination may replace.
		this.rootEl.setPointerCapture(event.pointerId)
		this.selectAt(event.clientY)
	}

	private handlePointerMove = (event: PointerEvent): void => {
		if (event.pointerId !== this.pointerId) return
		event.preventDefault()
		event.stopPropagation()
		this.selectAt(event.clientY)
	}

	private handlePointerEnd = (event: PointerEvent): void => {
		if (event.pointerId !== this.pointerId) return
		event.preventDefault()
		event.stopPropagation()
		if (event.type === 'pointerup') this.selectAt(event.clientY)
		this.cancelDrag()
	}

	private handleClick = (event: MouseEvent): void => {
		// Padding and post-drag clicks belong to the index, never the release behind it.
		event.preventDefault()
		event.stopPropagation()
		if (event.detail !== 0 || !(event.target instanceof Element)) return
		const button = event.target.closest<HTMLButtonElement>(
			'button[data-collection-side-index-target]',
		)
		if (button) this.navigate(Number(button.dataset.indexFrom), 'smooth')
	}

	private selectAt(clientY: number): void {
		const buttons =
			this.rootEl.querySelectorAll<HTMLButtonElement>('button')
		let closest: HTMLButtonElement | null = null
		let closestDistance = Number.POSITIVE_INFINITY
		for (const button of buttons) {
			const rect = button.getBoundingClientRect()
			const distance = Math.abs(clientY - (rect.top + rect.height / 2))
			if (distance < closestDistance) {
				closest = button
				closestDistance = distance
			}
		}
		if (!closest) return
		const rect = closest.getBoundingClientRect()
		const from = Number(closest.dataset.indexFrom)
		const to = Number(closest.dataset.indexTo)
		// A dot represents a range: sliding within it still visits every omitted group.
		const fraction = Math.max(
			0,
			Math.min(0.999, (clientY - rect.top) / rect.height),
		)
		const groupIndex = from + Math.floor(fraction * (to - from + 1))
		if (groupIndex === this.selectedGroupIndex) return
		this.selectedButton?.classList.remove('is-active')
		this.selectedButton = closest
		closest.classList.add('is-active')
		void haptic('select')
		this.navigate(groupIndex, 'instant')
	}

	private handleResize = (): void => {
		this.render(this.pendingGroups ?? this.groups)
	}

	private scheduleOffsetUpdate = (): void => {
		if (this.offsetFrame) return
		this.offsetFrame = requestAnimationFrame(this.updateOffset)
	}

	// Keep the index below the controls at the top, centering once they scroll away.
	private updateOffset = (): void => {
		this.offsetFrame = 0
		// Moving the letters beneath a held finger would change the selection.
		if (this.pointerId !== null || !this.listEl) return
		if (this.rootEl.hidden || this.rootEl.closest('[hidden]')) return
		const current =
			Number.parseFloat(
				this.rootEl.style.getPropertyValue('--side-index-push'),
			) || 0
		const rect = this.rootEl.getBoundingClientRect()
		const navigationTop =
			document.getElementById('navigation')?.getBoundingClientRect()
				.top ?? window.innerHeight
		const push = Math.max(
			0,
			Math.min(
				this.listEl.getBoundingClientRect().top - (rect.top - current),
				navigationTop - NAVIGATION_CLEARANCE - (rect.bottom - current),
			),
		)
		this.rootEl.style.setProperty('--side-index-push', `${push}px`)
	}

	private handleKeydown = (event: KeyboardEvent): void => {
		if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
		if (!(event.target instanceof HTMLButtonElement)) return
		event.preventDefault()
		const from = Number(event.target.dataset.indexFrom)
		const to = Number(event.target.dataset.indexTo)
		const current =
			this.selectedGroupIndex !== null &&
			this.selectedGroupIndex >= from &&
			this.selectedGroupIndex <= to
				? this.selectedGroupIndex
				: from
		const next =
			event.key === 'Home'
				? 0
				: event.key === 'End'
					? this.groups.length - 1
					: Math.max(
							0,
							Math.min(
								this.groups.length - 1,
								current + (event.key === 'ArrowDown' ? 1 : -1),
							),
						)
		for (const button of this.rootEl.querySelectorAll<HTMLButtonElement>(
			'button',
		)) {
			if (
				Number(button.dataset.indexFrom) <= next &&
				Number(button.dataset.indexTo) >= next
			) {
				button.focus({ preventScroll: true })
				break
			}
		}
		this.navigate(next, 'instant')
	}

	private maxSlots(): number {
		const style = getComputedStyle(this.rootEl)
		const gap = Number.parseFloat(style.rowGap)
		const height =
			Number.parseFloat(style.maxHeight) -
			Number.parseFloat(style.paddingTop) -
			Number.parseFloat(style.paddingBottom)
		const itemHeight = Number.parseFloat(style.lineHeight)
		return Math.max(
			3,
			Math.min(
				MAX_INDEX_SLOTS,
				Math.floor((height + gap) / (itemHeight + gap)),
			),
		)
	}

	private navigate(groupIndex: number, behavior: ScrollBehavior): void {
		const group = this.groups[groupIndex]
		if (!group) return
		this.selectedGroupIndex = groupIndex
		document
			.getElementById(group.id)
			?.scrollIntoView({ block: 'start', behavior })
	}
}

function buildIndexSlots(count: number, maxSlots: number): IndexSlot[] {
	if (count <= maxSlots)
		return Array.from({ length: count }, (_, index) => ({
			from: index,
			to: index,
		}))
	const anchors = Math.floor((maxSlots + 1) / 2)
	const slots: IndexSlot[] = []
	for (let index = 0; index < anchors; index++) {
		const groupIndex = Math.round((index * (count - 1)) / (anchors - 1))
		const previous = slots.at(-1)
		if (previous && groupIndex > previous.to + 1)
			slots.push({ from: previous.to + 1, to: groupIndex - 1 })
		slots.push({ from: groupIndex, to: groupIndex })
	}
	return slots
}
